import { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import MissingContent from "../../../../components/shared/errorMessages/MissingContent";
import { useNavigate } from 'react-router-dom';
import {
  MediaOption,
  CarouselMediaItem,
  CMSViewMode
} from '../../types/CMSTypes';
import { getCategoryDetails, buildCategoryUrl } from '../../api/cmsApi';

// Props interface for CMS Consumer Banner
interface CMSConsumerCarouselBannerProps {
  // CMS Consumer Banner Data
  bannerMediaOption: MediaOption;
  bannerMediaCarousel: CarouselMediaItem[];
  bannerMediaVideo: string | null;
  bannerMediaVideoLink: string;
  bannerLinkCategoryId: string | null;
  bannerWidth: number;
  bannerHeight: number;

  // View mode for determining API behavior
  viewMode?: CMSViewMode;

  // Optional styling
  aspectRatio?: string; // Fallback if dimensions not available
}

const CMSConsumerCarouselBanner: React.FC<CMSConsumerCarouselBannerProps> = ({
                                                                               bannerMediaOption = 'carousel',
                                                                               bannerMediaCarousel = [],
                                                                               bannerMediaVideo = null,
                                                                               bannerMediaVideoLink = '',
                                                                               bannerLinkCategoryId = null,
                                                                               bannerWidth = 2880,
                                                                               bannerHeight = 1050,
                                                                               viewMode = 'live',
                                                                               aspectRatio = "36.46%" // Default fallback (1050/2880 * 100)
                                                                             }) => {
  const navigate = useNavigate();

  // Calculate dynamic aspect ratio from banner dimensions
  const calculateAspectRatio = (): string => {
    if (bannerWidth && bannerHeight && bannerWidth > 0 && bannerHeight > 0) {
      const calculatedRatio = (bannerHeight / bannerWidth) * 100;
      return `${calculatedRatio}%`;
    }
    return aspectRatio;
  };

  const dynamicAspectRatio = calculateAspectRatio();

  // Helper function to create category link
  const createCategoryLink = async (categoryId: string): Promise<string> => {
    try {
      const categoryResponse = await getCategoryDetails(categoryId, viewMode);
      if (categoryResponse.success && categoryResponse.cms_category) {
        return buildCategoryUrl(categoryResponse.cms_category);
      }
    } catch (error) {
      console.error('Error building category link:', error);
    }

    // Fallback to simple category link
    return `/category/${categoryId}`;
  };

  // Handle click navigation
  const handleItemClick = async () => {
    if (bannerLinkCategoryId) {
      const categoryLink = await createCategoryLink(bannerLinkCategoryId);
      navigate(categoryLink);
    }
  };

  // Build items array based on media option
  const buildItemsArray = () => {
    const items: Array<{url: string; isVideo: boolean; hasLink: boolean}> = [];

    switch (bannerMediaOption) {
      case 'video':
        // Video first, then carousel as fallback
        if (bannerMediaVideo && bannerMediaVideoLink) {
          items.push({
            url: bannerMediaVideoLink,
            isVideo: true,
            hasLink: !!bannerLinkCategoryId
          });
        }
        // Add carousel items as fallback
        bannerMediaCarousel.forEach(item => {
          if (item.media_url) {
            items.push({
              url: item.media_url,
              isVideo: false,
              hasLink: !!bannerLinkCategoryId
            });
          }
        });
        break;

      case 'carousel':
      case 'single':
      default:
        // Use carousel images
        bannerMediaCarousel.forEach(item => {
          if (item.media_url) {
            items.push({
              url: item.media_url,
              isVideo: false,
              hasLink: !!bannerLinkCategoryId
            });
          }
        });
        break;
    }

    return items;
  };

  const [items] = useState(() => buildItemsArray());
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Navigation functions
  const next = () => {
    if (items.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
  };

  const previous = () => {
    if (items.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length);
  };

  // Preload images
  useEffect(() => {
    const itemsToPreload = [
      items[currentIndex],
      items[(currentIndex + 1) % items.length]
    ].filter(Boolean);

    itemsToPreload.forEach((item) => {
      if (!item?.url || item.isVideo) return;

      const img = new Image();
      img.src = item.url;
    });
  }, [items, currentIndex]);

  // Auto-advance logic
  useEffect(() => {
    if (items.length <= 1) return;

    // Don't auto-advance if we're showing a single video
    if (bannerMediaOption === 'video' && items.length === 1 && items[0]?.isVideo) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % items.length);
    }, 3000); // 3 second intervals

    return () => clearInterval(interval);
  }, [items.length, bannerMediaOption]);

  // Handle video end
  const handleVideoEnd = () => {
    if (items.length > 1 && bannerMediaOption !== 'video') {
      next();
    } else if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {
        console.warn('Video replay failed');
      });
    }
  };

  // Determine if we should show navigation arrows
  const showNavigation = items.length > 1 &&
    !(bannerMediaOption === 'video' && items[0]?.isVideo);

  // If no items, show missing content
  if (items.length === 0) {
    return <MissingContent sectionName="Banner Carousel" />;
  }

  return (
    <Box sx={{
      marginTop: 0,
      paddingTop: 0,
      borderTop: 'none',
      display: 'block'
    }}>
      <Box
        sx={{
          width: "100%",
          position: "relative",
          overflow: "hidden",
          paddingTop: dynamicAspectRatio,
          marginTop: 0,
          borderTop: 'none',
          display: 'block'
        }}
      >
        {items.map((item, index) => (
          item && (
            <Box
              key={index}
              className={`slide ${index === currentIndex ? "active" : ""}`}
              sx={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                cursor: item.hasLink ? "pointer" : "default",
                transition: 'opacity 0.5s ease-in-out',
                opacity: index === currentIndex ? 1 : 0,
              }}
              onClick={handleItemClick}
            >
              {item.isVideo ? (
                <video
                  ref={videoRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    objectFit: "contain",
                  }}
                  onEnded={handleVideoEnd}
                  muted
                  autoPlay
                  playsInline
                  poster="" // Placeholder for thumbnail
                  preload="metadata"
                >
                  <source src={item.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    objectFit: "contain",
                  }}
                  src={item.url}
                  alt=""
                />
              )}
            </Box>
          )
        ))}

        {showNavigation && (
          <>
            <ArrowBackIosIcon
              sx={{
                position: "absolute",
                bottom: "50%",
                left: "5%",
                zIndex: 1,
                cursor: "pointer",
                color: "white",
                fontSize: "2rem"
              }}
              onClick={previous}
            />
            <ArrowForwardIosIcon
              sx={{
                position: "absolute",
                bottom: "50%",
                right: "5%",
                zIndex: 1,
                cursor: "pointer",
                color: "white",
                fontSize: "2rem"
              }}
              onClick={next}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default CMSConsumerCarouselBanner;